/**
 * VIN → vehicle details autofill via NHTSA vPIC (US DOT public API).
 * https://vpic.nhtsa.dot.gov/api/ — free, no key, CORS-enabled.
 *
 * Fires when the VIN reaches 17 valid characters. Best-effort only: any
 * failure leaves the form fully usable for manual entry. The VIN (and
 * nothing else) is sent to NHTSA — note for the privacy review.
 */

import type { VehicleCascade } from './vehicle-cascade'

const VIN_17 = /^[A-HJ-NPR-Z0-9]{17}$/
const API = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/'

/** Our picklist codes ↔ NHTSA Make names (vPIC returns uppercase names). */
const NHTSA_MAKE_TO_CODE: Record<string, string> = {
  ACURA: 'ACUR', AUDI: 'AUDI', BMW: 'BMW', BUICK: 'BUIC', CADILLAC: 'CADI',
  CHEVROLET: 'CHEV', CHRYSLER: 'CHRY', DODGE: 'DODG', FIAT: 'FIAT', FORD: 'FORD',
  GENESIS: 'GENE', GMC: 'GMC', HONDA: 'HOND', HYUNDAI: 'HYUN', INFINITI: 'INFI',
  ISUZU: 'ISU', JAGUAR: 'JAGU', JEEP: 'JEEP', KIA: 'KIA', 'LAND ROVER': 'LNDR',
  LEXUS: 'LEXS', LINCOLN: 'LINC', MAZDA: 'MAZD', MERCURY: 'MERC',
  'MERCEDES-BENZ': 'MERZ', MINI: 'MINI', MITSUBISHI: 'MITS', NISSAN: 'NISS',
  OLDSMOBILE: 'OLDS', PLYMOUTH: 'PLYM', PONTIAC: 'PONT', PORSCHE: 'PORS',
  RAM: 'RAM', SAAB: 'SAA', SATURN: 'STRN', SCION: 'SCIO', SUBARU: 'SUBA',
  SUZUKI: 'SUZI', TESLA: 'TESL', TOYOTA: 'TOYT', VOLKSWAGEN: 'VOLK', VOLVO: 'VOLV',
}

interface VpicResult {
  Make?: string
  Model?: string
  ModelYear?: string
  VehicleType?: string
  BodyClass?: string
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/(^|[\s-])\w/g, (c) => c.toUpperCase())
}

/** NHTSA VehicleType/BodyClass → our picklist. Returns [type, otherText]. */
function mapVehicleType(vehicleType: string, bodyClass: string): [string, string] {
  const vt = vehicleType.toUpperCase()
  const bc = bodyClass.toLowerCase()
  if (bc.includes('van')) return ['Van', '']
  if (bc.includes('sport utility') || bc.includes('crossover')) return ['SUV', '']
  if (bc.includes('pickup')) return ['Truck', '']
  if (vt.includes('PASSENGER CAR')) return ['Car', '']
  if (vt.includes('TRUCK')) return ['Truck', '']
  if (vt.includes('MULTIPURPOSE')) return ['SUV', '']
  if (vt) return ['Other', titleCase(bodyClass || vehicleType)]
  return ['', '']
}

type Status = 'loading' | 'success' | 'error' | 'idle'

function showVinStatus(status: Status, message: string): void {
  const el = document.getElementById('vinStatus')
  if (!el) return
  el.classList.toggle('hidden', status === 'idle')
  el.classList.toggle('loading', status === 'loading')
  el.classList.toggle('error', status === 'error')
  el.textContent = message
}

/** Set a field and fire 'change' so conditional logic / error clearing react. */
function setField(form: HTMLFormElement, name: string, value: string): void {
  const field = form.elements.namedItem(name)
  if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return
  if (field instanceof HTMLSelectElement && value && !Array.from(field.options).some((o) => o.value === value)) return
  field.value = value
  field.dispatchEvent(new Event('change', { bubbles: true }))
}

async function applyDecode(form: HTMLFormElement, cascade: VehicleCascade, r: VpicResult): Promise<boolean> {
  const make = (r.Make ?? '').trim().toUpperCase()
  const model = (r.Model ?? '').trim()
  const year = (r.ModelYear ?? '').trim()
  if (!make && !year) return false // nothing useful decoded

  const makeCode = NHTSA_MAKE_TO_CODE[make]
  await cascade.applyVinDecode(
    /^\d{4}$/.test(year) ? year : '',
    makeCode ?? (make ? 'OTHER' : ''),
    // Unmapped make (rare now): keep the real make visible in the model text.
    makeCode || !make ? model : `${titleCase(make)} ${model}`.trim(),
  )

  const [type, otherText] = mapVehicleType(r.VehicleType ?? '', r.BodyClass ?? '')
  if (type) {
    setField(form, 'vehicleType', type)
    if (type === 'Other' && otherText) setField(form, 'vehicleTypeOther', otherText)
  }
  return true
}

export function initVinLookup(form: HTMLFormElement, cascade: VehicleCascade): void {
  const vin = document.getElementById('vin') as HTMLInputElement | null
  if (!vin) return

  let lastLookup = ''
  let controller: AbortController | null = null

  vin.addEventListener('input', () => {
    // VINs are case-insensitive — normalize to uppercase as typed.
    const caret = vin.selectionStart
    const upper = vin.value.toUpperCase()
    if (upper !== vin.value) {
      vin.value = upper
      if (caret !== null) vin.setSelectionRange(caret, caret)
    }

    const value = vin.value.trim()
    if (!VIN_17.test(value)) {
      controller?.abort('superseded')
      showVinStatus('idle', '')
      return
    }
    if (value === lastLookup) return
    lastLookup = value
    void lookup(value)
  })

  async function lookup(value: string): Promise<void> {
    controller?.abort('superseded')
    const mine = new AbortController()
    controller = mine
    const timeout = setTimeout(() => mine.abort('timeout'), 8000)

    showVinStatus('loading', 'Looking up your VIN…')
    try {
      const res = await fetch(`${API}${encodeURIComponent(value)}?format=json`, { signal: mine.signal })
      if (!res.ok) throw new Error(`vpic ${res.status}`)
      const data = (await res.json()) as { Results?: VpicResult[] }
      const result = data.Results?.[0]

      if (result && (await applyDecode(form, cascade, result))) {
        showVinStatus('success', 'Vehicle details filled in below — please review them.')
      } else {
        showVinStatus('error', 'We couldn’t decode that VIN. Please check it, or fill in the details manually.')
      }
    } catch {
      if (mine.signal.aborted && mine.signal.reason === 'superseded') return // newer input took over
      showVinStatus('error', 'VIN lookup is unavailable right now. Please fill in the details manually.')
    } finally {
      clearTimeout(timeout)
    }
  }
}
